import { useContext } from "react";
import { Breadcrumb, Button, Card, Steps, Tag } from "antd";
import Title from "antd/es/typography/Title";
import { Link } from "react-router-dom";
import {
    FileTextOutlined,
    LeftOutlined,
    PlayCircleOutlined,
    RightOutlined,
    TrophyOutlined,
} from '@ant-design/icons';

import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import GiveawayStatusTag from "../components/GiveawayStatusTag";
import ImportData from "./Steps/ImportData";
import ExportData from "./Steps/ExportData";
import useHoldGiveawayAuto from "./useHoldGiveawayAuto";
import ModalWinners from "../../Giveaways/HoldGiveaway/components/ModalWinners";
import GiveawayLiveDrawModal from "../../Giveaways/HoldGiveaway/components/GiveawayLiveDrawModal";
import GiveawayHistoryCards from "./components/GiveawayHistoryCards";
import { ThemeContext } from "../../../contexts/ThemeContext";
import { convertDataUSParaBrasilEHora } from "../../../utils/Conversores";

export default function HoldGiveawayAuto() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        giveawayAuto,
        isLoadingGiveaway,
        currentStep,
        handleClickExportStep,
        handleClickImportStep,
        handleClickNextStep,
        handleClickPreviousStep,
        handleTicketsFileGenerated,
        handleWinnersFileImported,
        alreadyGeneratedTicketsFile,
        panelUserGeneratedParcitipantsFile,
        panelUserUploadedWinnersFile,
        lastGiveawayHistory,
        handleClickViewAwards,
        tableData,
        handleCloseModalWinners,
        showWinners,
        awardsWithWinners,
        rulesGiveaway,
        handleClickDownloadTicketsFile,
        getWinnersByGiveawayAndAttempt,
        isDownloadFile,
        showLiveDraw,
        liveDrawAttempt,
        awardsForLiveDraw,
        isLoadingLiveDraw,
        handleOpenLiveDraw,
        handleCloseLiveDraw,
    } = useHoldGiveawayAuto();

    const getSubtitle = () => {
        if (!giveawayAuto) return "Gere o arquivo de bilhetes e importe o resultado oficial da apuração";
        const parts: string[] = [];
        if (giveawayAuto.period_beginning && giveawayAuto.period_ending) {
            parts.push(`Período: ${convertDataUSParaBrasilEHora(giveawayAuto.period_beginning)} até ${convertDataUSParaBrasilEHora(giveawayAuto.period_ending)}`);
        }
        if (giveawayAuto.date) {
            parts.push(`Data do sorteio: ${convertDataUSParaBrasilEHora(giveawayAuto.date)}`);
        }
        return parts.join(' • ');
    };

    return (
        <Loading loading={isLoadingGiveaway}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* External Breadcrumb Card */}
                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 10,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Breadcrumb
                        items={[
                            {
                                title: <Link to='/sorteios/auto'>Sorteios Automáticos</Link>,
                            },
                            {
                                title: <Link to={`/sorteios/auto/visualizar/${giveawayAuto?.id}`}>{giveawayAuto?.description || 'Visualizar'}</Link>,
                            },
                            {
                                title: 'Realizar Sorteio',
                            }
                        ]}
                    />
                </div>

                <PageHeader
                    icon={<PlayCircleOutlined />}
                    title={giveawayAuto?.description ? `Realizar Sorteio - ${giveawayAuto.description}` : "Realizar Sorteio"}
                    subtitle={getSubtitle()}
                    tag={
                        giveawayAuto ? (
                            <GiveawayStatusTag
                                status={giveawayAuto.status}
                                isValid={giveawayAuto.is_valid}
                            />
                        ) : undefined
                    }
                    actions={
                        giveawayAuto && (
                            <Button
                                type="default"
                                icon={<TrophyOutlined style={{ color: '#faad14' }} />}
                                onClick={handleClickViewAwards}
                                style={{
                                    borderRadius: 8,
                                    fontWeight: 500,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                Ver Prêmios
                            </Button>
                        )
                    }
                />

                {/* Steps Section Card */}
                <Card
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 14,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    }}
                    bodyStyle={{ padding: '24px 28px' }}
                >
                    <div style={{ maxWidth: 700, margin: '0 auto 28px auto' }}>
                        <Steps
                            current={currentStep}
                            items={[
                                {
                                    title: "Exportar Bilhetes",
                                    icon: <FileTextOutlined />,
                                    onClick: handleClickExportStep,
                                    style: { cursor: 'pointer' },
                                },
                                {
                                    title: "Importar Resultado",
                                    icon: <TrophyOutlined />,
                                    onClick: alreadyGeneratedTicketsFile ? handleClickImportStep : undefined,
                                    disabled: !alreadyGeneratedTicketsFile,
                                    style: { cursor: alreadyGeneratedTicketsFile ? 'pointer' : 'not-allowed' },
                                },
                            ]}
                        />
                    </div>

                    <div style={{ minHeight: 180 }}>
                        {currentStep === 0 && (
                            <ExportData
                                giveawayAuto={giveawayAuto}
                                lastGiveawayHistory={lastGiveawayHistory}
                                panelUserGeneratedParticipantsFile={panelUserGeneratedParcitipantsFile}
                                onTicketsFileGenerated={handleTicketsFileGenerated}
                                key={giveawayAuto?.id}
                            />
                        )}

                        {currentStep === 1 && (
                            <ImportData
                                giveawayAuto={giveawayAuto}
                                lastGiveawayHistory={lastGiveawayHistory}
                                panelUserUploadedWinnersFile={panelUserUploadedWinnersFile}
                                onWinnersFileImported={handleWinnersFileImported}
                                key={giveawayAuto?.id}
                            />
                        )}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginTop: 28,
                            paddingTop: 20,
                            borderTop: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        }}
                    >
                        <Button
                            type="default"
                            icon={<LeftOutlined />}
                            disabled={currentStep === 0}
                            onClick={handleClickPreviousStep}
                            style={{
                                borderRadius: 8,
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            Anterior
                        </Button>

                        <Button
                            type="primary"
                            disabled={currentStep === 1 || !alreadyGeneratedTicketsFile}
                            onClick={handleClickNextStep}
                            style={{
                                borderRadius: 8,
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            Próximo <RightOutlined />
                        </Button>
                    </div>
                </Card>

                {/* History Section - Modern Cards */}
                <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Title level={4} style={{ margin: 0, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                                Histórico do Sorteio
                            </Title>
                            <Tag color="blue" style={{ borderRadius: 12, fontWeight: 600 }}>
                                {tableData.length} {tableData.length === 1 ? 'registro' : 'registros'}
                            </Tag>
                        </div>
                    </div>

                    <GiveawayHistoryCards
                        historyList={tableData}
                        onDownloadFile={handleClickDownloadTicketsFile}
                        onViewWinners={getWinnersByGiveawayAndAttempt}
                        onShowLiveDraw={handleOpenLiveDraw}
                        isDownloadingFile={isDownloadFile}
                        loading={isLoadingGiveaway}
                    />
                </div>

                <ModalWinners
                    onClose={handleCloseModalWinners}
                    rules={rulesGiveaway}
                    open={showWinners}
                    awardsWithWinners={awardsWithWinners || []}
                    giveawayTitle={giveawayAuto?.description}
                />

                <GiveawayLiveDrawModal
                    open={showLiveDraw}
                    onClose={handleCloseLiveDraw}
                    giveawayTitle={giveawayAuto?.description}
                    giveawayDate={giveawayAuto?.date}
                    attemptNumber={liveDrawAttempt || undefined}
                    awards={awardsForLiveDraw}
                    rules={rulesGiveaway}
                    loading={isLoadingLiveDraw}
                />
            </div>
        </Loading>
    );
}
